import React, { useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { Loader } from '@playbookux/playbook-web-core';
import withLayout from '../../../core/components/with-layout';
import Tabs from '../../constants/tabs';

import CardsHighlights from '../../../core/components/cards-highlights';
import ProjectsDropdown from '../../../core/components/projects-dropdown';
import useSearch from '../../../core/hooks/use-search';

import {
  Container,
  FilterContainer,
  SearchField,
  TitleContainer,
  MenuContainer,
  MenuFilter,
  MenuFilterItem,
  Center,
  CardsContainer,
  ContainerDropdown,
  ZeroState,
  Text,
  Message,
  SearchImgIcon,
  NoResultFind,
} from './styles';

const SearchTypes = {
  ALL: 'All',
  TRANSCRIPTS: 'Transcripts',
  NOTES: 'Notes',
  CLIPS: 'Clips',
  REPORTS: 'Reports',
  HIGHLIGHTS: 'Highlights',
};

const Search = (props) => {
  const {
    companyId,
  } = props;

  const [activeItem, setActiveItem] = useState(SearchTypes.ALL);
  const [selectedProjects, setSelectedProjects] = useState(null);
  const [searchInput, setSearchInput] = useState('');

  const handleItemClick = (e, { name }) => setActiveItem(name);

  const handleSearchInput = (e, { value }) => {
    setSearchInput(value);
  };

  const {
    loading,
    dataSearch = {},
    fetchMore,
    hasMore,
  } = useSearch(companyId, searchInput, selectedProjects, activeItem);

  const { search = [] } = dataSearch;

  const handleLoadMore = () => {
    fetchMore();
  };

  const handleClearButton = () => {
    setActiveItem(SearchTypes.ALL);
    setSearchInput('');
  };

  const hasSearched = searchInput.length > 0;

  return (
    <Container>
      <TitleContainer>
        <FilterContainer>

          <SearchField
            placeholder="Type to search"
            input={{ icon: 'search', iconPosition: 'left' }}
            loading={loading}
            onSearchChange={handleSearchInput}
            value={searchInput}
            showClear={hasSearched}
            onClear={handleClearButton}
          />

          <ContainerDropdown>
            <ProjectsDropdown
              companyId={companyId}
              setSelectedProjects={setSelectedProjects}
            />
          </ContainerDropdown>
        </FilterContainer>
      </TitleContainer>

      <MenuContainer>
        <MenuFilter secondary>
          {_.map(SearchTypes, (item) => (
            <MenuFilterItem
              name={item}
              active={activeItem === item}
              onClick={handleItemClick}
            />
          ))}
        </MenuFilter>
      </MenuContainer>

      {!loading && !searchInput && _.isEmpty(search) && (
        <ZeroState>
          <SearchImgIcon
            src="/images/search-icon.png"
          />
          <Text as="h2">
            Search your research
          </Text>
          <Message fluid>
            Quickly find notes, clips, transcripts and reports across projects
          </Message>
        </ZeroState>
      )}

      {loading && (
        <Center>
          <Loader data-loader="search-loader" />
        </Center>
      )}

      {!loading && searchInput && _.isEmpty(search) && (
        <NoResultFind>
          <Text as="h2">
            {`No results for “${searchInput}”`}
          </Text>
          <Message>
            You may want to try using different keywords, checking for typos, or adjusting your filters.
          </Message>
        </NoResultFind>
      )}

      <CardsContainer>
        {!_.isEmpty(search) && (
          <CardsHighlights
            hasMore={hasMore}
            highlights={search}
            onLoadMore={handleLoadMore}
            typeHighlight="search"
            searchedText={searchInput}
          />
        )}
      </CardsContainer>

    </Container>
  );
};

Search.propTypes = {
  companyId: PropTypes.string.isRequired,
};

const mapStateToProps = ({ authentication }) => ({
  companyId: authentication.company?.id,
});

const enhance = compose(
  withLayout({ selectedTab: Tabs.SEARCH }),
  connect(mapStateToProps, null),
);

export default enhance(Search);
